'use strict';

/*
 * waitlist-popup.js
 *
 * First-time-visitor popup.
 *   - Desktop: exit-intent OR <delay> seconds, whichever fires first.
 *   - Mobile:  <delay> seconds OR <scroll %> of page height.
 *   - Once per visitor (localStorage + 30-day cookie fallback).
 *   - Suppressed if: ?no-popup in URL, focus is in a form/input, or
 *     within 10s of load.
 *   - Submits via fetch and shows success only when Shopify confirms the
 *     signup; bot-protection challenges fall back to a normal form post.
 *   - Focus-trapped dialog; ESC/overlay/X to close; dismissal counts as "seen".
 */

(function () {
  var root = document.querySelector('[data-waitlist-popup]');
  if (!root) return;

  // ── Config ─────────────────────────────────────────────────────────
  var STORAGE_KEY      = root.dataset.storageKey || 'armorbands_waitlist_popup_seen_v1';
  var COOKIE_KEY       = STORAGE_KEY + '_cookie';
  var COOKIE_DAYS      = 30;
  var LOAD_GRACE_MS    = 10 * 1000;
  var DELAY_DESKTOP_MS = (parseInt(root.dataset.delayDesktop, 10) || 20) * 1000;
  var DELAY_MOBILE_MS  = (parseInt(root.dataset.delayMobile,  10) || 15) * 1000;
  var SCROLL_THRESHOLD = (parseInt(root.dataset.scrollThreshold, 10) || 50) / 100;
  var IS_TOUCH_MOBILE  = window.matchMedia('(hover: none) and (pointer: coarse)').matches
                         || window.innerWidth < 720;
  var ERROR_MSG        = root.dataset.errorMessage || 'That didn\u2019t go through. Please check your email and try again.';
  var EXISTING_MSG     = root.dataset.existingMessage || 'Looks like you\u2019re already on our list \u2013 welcome back.';

  // Back from a normal form post (bot-protection fallback below): Shopify
  // redirects to ?customer_posted=true#WaitlistPopupForm on success.
  var returnedFromPost = /[?&]customer_posted=true/.test(window.location.search)
                         && window.location.hash === '#WaitlistPopupForm';

  var loadedAt = Date.now();
  var opened   = false;
  var timers   = [];

  // ── Persistence helpers ────────────────────────────────────────────
  function safeGet(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  }
  function safeSet(key, val) {
    try { window.localStorage.setItem(key, val); } catch (e) {}
  }
  function getCookie(name) {
    var m = document.cookie.match('(^|;)\\s*' + name + '=([^;]+)');
    return m ? m.pop() : '';
  }
  function setCookie(name, value, days) {
    var d = new Date(); d.setTime(d.getTime() + days * 864e5);
    document.cookie = name + '=' + value + ';expires=' + d.toUTCString() + ';path=/;SameSite=Lax';
  }
  function hasBeenSeen() {
    return safeGet(STORAGE_KEY) === '1' || getCookie(COOKIE_KEY) === '1';
  }
  function markSeen() {
    safeSet(STORAGE_KEY, '1');
    setCookie(COOKIE_KEY, '1', COOKIE_DAYS);
  }

  // ── Bail-early conditions ──────────────────────────────────────────
  if (hasBeenSeen() && !returnedFromPost) return;
  if (/[?&]no-popup(=|&|$)/.test(window.location.search)) return;

  // If the Shopify form already posted successfully, the section's
  // inline script already opened the popup in success mode — don't
  // double-fire.
  if (root.classList.contains('waitlist-popup--success')) return;

  // ── Open / close ───────────────────────────────────────────────────
  var dialog  = root.querySelector('.waitlist-popup__dialog');
  var overlay = root.querySelector('[data-waitlist-popup-overlay]');
  var closers = root.querySelectorAll('[data-waitlist-popup-close]');
  var emailIn = root.querySelector('[data-waitlist-popup-email]');
  var lastFocusedBeforeOpen = null;

  function inFocusableField() {
    var a = document.activeElement;
    if (!a) return false;
    var tag = (a.tagName || '').toLowerCase();
    if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
    if (a.isContentEditable) return true;
    return false;
  }

  function openPopup(reason) {
    if (opened) return;
    if (Date.now() - loadedAt < LOAD_GRACE_MS) return;
    if (inFocusableField()) {
      // Defer — try again in 4s so we don't interrupt typing
      timers.push(setTimeout(function () { openPopup(reason); }, 4000));
      return;
    }

    opened = true;
    clearAllTimers();
    lastFocusedBeforeOpen = document.activeElement;

    root.hidden = false;
    root.setAttribute('aria-hidden', 'false');
    // Force reflow before adding the animation class
    void root.offsetWidth;
    root.classList.add('waitlist-popup--open');

    // Lock scroll behind the modal
    document.documentElement.style.overflow = 'hidden';

    // Move focus into the dialog (the email input is the likely next action)
    setTimeout(function () {
      if (emailIn && typeof emailIn.focus === 'function') emailIn.focus();
      else if (dialog) dialog.focus();
    }, 50);

    // Analytics-style breadcrumb — easy to hook into later
    document.dispatchEvent(new CustomEvent('waitlist:popup_opened', { detail: { reason: reason } }));
  }

  function closePopup() {
    if (!opened) return;
    opened = false;
    root.classList.remove('waitlist-popup--open');
    root.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';

    setTimeout(function () {
      root.hidden = true;
      if (lastFocusedBeforeOpen && typeof lastFocusedBeforeOpen.focus === 'function') {
        try { lastFocusedBeforeOpen.focus(); } catch (e) {}
      }
    }, 260);

    markSeen();
    document.dispatchEvent(new CustomEvent('waitlist:popup_closed'));
  }

  // ── Focus trap ─────────────────────────────────────────────────────
  function focusableChildren() {
    if (!dialog) return [];
    return Array.prototype.slice.call(dialog.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter(function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
  }
  function onKeydown(e) {
    if (!opened) return;
    if (e.key === 'Escape') { e.preventDefault(); closePopup(); return; }
    if (e.key !== 'Tab') return;
    var nodes = focusableChildren();
    if (!nodes.length) return;
    var first = nodes[0], last = nodes[nodes.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  }

  document.addEventListener('keydown', onKeydown);
  if (overlay) overlay.addEventListener('click', closePopup);
  Array.prototype.forEach.call(closers, function (btn) {
    btn.addEventListener('click', function (e) { e.preventDefault(); closePopup(); });
  });

  // ── Triggers ───────────────────────────────────────────────────────
  function clearAllTimers() {
    timers.forEach(clearTimeout);
    timers = [];
  }

  // Delay trigger
  var delayMs = IS_TOUCH_MOBILE ? DELAY_MOBILE_MS : DELAY_DESKTOP_MS;
  if (!returnedFromPost) timers.push(setTimeout(function () { openPopup('delay'); }, delayMs));

  // ── AJAX form submit ──────────────────────────────────────────────
  // Shopify's {% form 'customer' %} normally POSTs and reloads the page,
  // so the visitor would never see the popup's success state. We submit
  // via fetch so the page stays put — but only report success when
  // Shopify confirms it, and hand bot-protection challenges back to a
  // normal form post.
  var waitlistForm = root.querySelector('#WaitlistPopupForm');
  var formWrapEl   = root.querySelector('[data-waitlist-popup-form]');
  var successEl    = root.querySelector('[data-waitlist-popup-success]');
  var errorEl      = root.querySelector('.waitlist-popup__error');

  function showSuccess() {
    root.classList.add('waitlist-popup--success');
    if (formWrapEl) { formWrapEl.hidden = true;  formWrapEl.style.display = 'none'; }
    if (successEl)  { successEl.hidden  = false; successEl.style.display  = 'flex'; }
    markSeen();
    document.dispatchEvent(new CustomEvent('waitlist:submitted'));
  }

  function showError(msg, isInfo) {
    if (!errorEl) {
      errorEl = document.createElement('p');
      errorEl.className = 'waitlist-popup__error';
      errorEl.setAttribute('role', 'alert');
      var inputRow = root.querySelector('.waitlist-popup__input-row');
      if (inputRow && inputRow.parentNode) inputRow.parentNode.insertBefore(errorEl, inputRow.nextSibling);
    }
    errorEl.textContent = msg || ERROR_MSG;
    errorEl.classList.toggle('waitlist-popup__error--info', !!isInfo);
    errorEl.style.display = 'block';
  }

  // Normal (non-fetch) post, e.g. when Shopify's bot protection needs the
  // visitor to complete a challenge page. form.submit() skips our handler.
  function submitNatively() {
    markSeen();
    HTMLFormElement.prototype.submit.call(waitlistForm);
  }

  // Pull Shopify's error text out of the page it sent back.
  function errorFromResponse(html) {
    try {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var candidates = doc.querySelectorAll('#WaitlistPopupForm .waitlist-popup__error, #WaitlistPopupForm .errors, .errors');
      for (var i = 0; i < candidates.length; i++) {
        var msg = candidates[i].textContent.replace(/\s+/g, ' ').trim();
        if (msg) return msg;
      }
      return '';
    } catch (e) { return ''; }
  }

  if (waitlistForm) {
    waitlistForm.addEventListener('submit', function (e) {
      e.preventDefault();
      var submitBtn = waitlistForm.querySelector('button[type="submit"]');
      var originalLabel = submitBtn ? submitBtn.textContent : '';
      if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Joining…'; }
      if (errorEl) { errorEl.style.display = 'none'; }

      var formData = new FormData(waitlistForm);
      // Shopify's customer form expects form_type and utf8; FormData from the
      // rendered {% form %} already includes these hidden inputs, but enforce
      // defensively in case a theme variant strips them.
      if (!formData.get('form_type')) formData.append('form_type', 'customer');
      if (!formData.get('utf8'))      formData.append('utf8', '\u2713');

      fetch(waitlistForm.getAttribute('action') || '/contact', {
        method: 'POST',
        body: formData,
        headers: { 'Accept': 'text/html,application/xhtml+xml' },
        credentials: 'same-origin',
        redirect: 'follow'
      }).then(function (res) {
        var finalUrl = res.url || '';
        // Bot protection: Shopify sends suspicious posts to a challenge page.
        if (/\/challenge(?:[/?#]|$)/.test(finalUrl)) { submitNatively(); return; }
        // Success: Shopify redirects back with ?customer_posted=true.
        if (/[?&]customer_posted=true/.test(finalUrl)) { showSuccess(); return; }
        // Anything else is an error page — surface Shopify's message.
        return res.text().then(function (html) {
          var msg = errorFromResponse(html);
          if (/taken|already/i.test(msg)) showError(EXISTING_MSG, true);
          else showError(ERROR_MSG);
        });
      }).catch(function () {
        showError(ERROR_MSG);
      }).finally(function () {
        if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = originalLabel; }
      });
    });
  }

  if (returnedFromPost) {
    // Signup completed via the normal-post fallback — show the success state.
    showSuccess();
    loadedAt = 0;
    openPopup('returned');
  } else if (IS_TOUCH_MOBILE) {
    // Scroll-depth trigger
    var scrollHandler = function () {
      var doc = document.documentElement;
      var scrolled = (window.scrollY || doc.scrollTop);
      var maxScroll = (doc.scrollHeight - window.innerHeight);
      if (maxScroll <= 0) return;
      if ((scrolled / maxScroll) >= SCROLL_THRESHOLD) {
        window.removeEventListener('scroll', scrollHandler);
        openPopup('scroll');
      }
    };
    window.addEventListener('scroll', scrollHandler, { passive: true });
  } else {
    // Desktop exit-intent — mouse crossing into the top 6% of the viewport
    var exitHandler = function (e) {
      if (e.clientY <= Math.max(12, window.innerHeight * 0.06) && e.relatedTarget === null) {
        document.removeEventListener('mouseout', exitHandler);
        openPopup('exit-intent');
      }
    };
    // Small delay before wiring exit-intent so quick mouse-outs during initial
    // page positioning don't count.
    timers.push(setTimeout(function () {
      document.addEventListener('mouseout', exitHandler);
    }, Math.min(4000, LOAD_GRACE_MS / 2)));
  }
})();
